import axios from "axios";
import { COMMON_CONSTANTS } from "~/constants/common-constants";
import type { IRefreshTokenResponse } from "~/types/common";
import { getBaseUrl } from "~/lib/config/host";

export default defineNuxtPlugin((nuxtApp) => {
  const baseUrl = getBaseUrl();
  const { showSnackbar } = useSnackbar();

  // Setup token cookies
  const token = useCookie("Token", {
    maxAge: 60 * 60 * 24 * 7,
    path: "/",
    sameSite: "strict",
  });

  const refreshToken = useCookie("RefreshToken", {
    maxAge: 60 * 60 * 24 * 7, // 7 days
    path: "/",
    sameSite: "strict",
  });

  // Flag to indicate a token refresh in progress and queue for pending requests
  let isRefreshing = false;
  let refreshQueue: Array<{
    originalRequest: any;
    resolve: (value: any) => void;
    reject: (reason?: any) => void;
  }> = [];

  // Create an Axios instance with default headers and baseURL
  const api = axios.create({
    baseURL: baseUrl,
    headers: {
      "X-Requested-With": "XMLHttpRequest",
      "Content-Type": "application/json",
      Accept: "application/json",
    },
  });

  // Request interceptor: Attach the token to each outgoing request (if available)
  api.interceptors.request.use(
    (config) => {
      if (token.value) {
        config.headers = config.headers || {};
        config.headers["Authorization"] = `Bearer ${token.value}`;
      }
      return config;
    },
    (error) => Promise.reject(error),
  );

  // Response interceptor for handling responses and token refreshing logic
  api.interceptors.response.use(
    (response) => {
      // Extract endpoint from the URL (using the config URL)
      const url = response.config.url || "";
      const segments = url.split("/");
      const endpoint = segments[segments.length - 1];
      const authUrls = ["login", "verify-otp"];

      // If response comes from login/verify-otp endpoints and tokens are present, update cookies
      if (
        authUrls.includes(endpoint) &&
        response.data?.data?.authorization !== "" &&
        response.data?.data?.refresh_token !== ""
      ) {
        token.value = response.data.data.authorization;
        refreshToken.value = response.data.data.refresh_token;
      }
      return response.data;
    },
    async (error) => {
      const { response, config } = error;

      // Check if error is due to an expired/invalid token
      if (
        response &&
        response.status === 401 &&
        response.data?.message === "Unauthenticated."
      ) {
        return new Promise((resolve, reject) => {
          // Queue the failed request
          refreshQueue.push({ originalRequest: config, resolve, reject });

          // If no refresh is in progress, initiate one
          if (!isRefreshing) {
            isRefreshing = true;

            if (!refreshToken.value) {
              reject(new Error("Refresh token is empty"));
              return;
            }

            // Call the refresh token API
            axios
              .post<IRefreshTokenResponse>(
                `${baseUrl}/refreshing-tokens`,
                { refresh_token: refreshToken.value },
                {
                  headers: {
                    "Content-Type": "application/json",
                  },
                },
              )
              .then((res) => {
                // Update tokens with new values
                token.value = res.data.data.access_token;
                refreshToken.value = res.data.data.refresh_token;

                // Retry all queued requests with the new token
                refreshQueue.forEach((queued) => {
                  queued.originalRequest.headers["Authorization"] =
                    `Bearer ${token.value}`;
                  api
                    .request(queued.originalRequest)
                    .then(queued.resolve)
                    .catch(queued.reject);
                });
                refreshQueue = [];
              })
              .catch((refreshError) => {
                // Reject all queued requests and clear cookies on refresh failure
                refreshQueue.forEach((queued) => queued.reject(refreshError));
                refreshQueue = [];
                useCookie("Token", {
                  maxAge: 0,
                  path: "/",
                  sameSite: "strict",
                }).value = "";
                useCookie("RefreshToken", {
                  maxAge: 0,
                  path: "/",
                  sameSite: "strict",
                }).value = "";
                navigateTo("/login");
                showSnackbar(COMMON_CONSTANTS.SESSION_EXPIRED, "error");
                reject(refreshError);
              })
              .finally(() => {
                isRefreshing = false;
              });
          }
        });
      }
      return Promise.reject(error);
    },
  );

  // Provide the Axios instance to the Nuxt app
  nuxtApp.provide("api", api);
});
